import React, { useState } from "react";
import magnifyingGlass from "../../../assets/images/headerImages/magnifying_glass_small_outline.svg";
import DateField from "../../../common/formFields/DateField";
import {
    dateFieldValueToString,
    INVALID_DATE_MESSAGE,
    isCompleteCalendarDate,
    QuickEntryFieldKind,
    sanitizeFieldInput,
} from "./quickEntryFieldConstraints";
import { INVALID_DATE } from "../../../common/constants/ApiConstants";
import "./InlineSearchForm.scss";

export type InlineSearchTag = "REQUIRED" | "PRIMARY";

export interface InlineSearchField {
    readonly name: string;
    readonly label: string;
    readonly value?: string;
    /** Field-importance tag shown next to the label (master-demo-v6 look): REQUIRED (patient) / PRIMARY (requestor fax+email). */
    readonly tag?: InlineSearchTag;
    /**
     * Input kind. "date" renders the shared masked DateField (MM/DD/YYYY
     * auto-format on type, same primitive classic + the QE Auth/Request/Service
     * date rows use) instead of a plain text input, so e.g. the patient DOB
     * search field masks like every other date field. Defaults to "text".
     *
     * Every other kind routes keystrokes AND pastes through the shared
     * sanitizeFieldInput (quickEntryFieldConstraints.ts) - the SAME pathway the
     * add-new/edit forms use, so both panes clean input identically:
     *   "phone"      AB#35252 - normalizePhone; a fax box cannot accept letters or
     *                specials, digits auto-format to XXX-XXX-XXXX (AB#35154's mask)
     *   "postalCode" AB#35253 - digits + the ZIP+4 hyphen, capped at 10
     * Deliberately opt-in per field: "email" must keep accepting letters and "@".
     *
     * No field declares postalCode today - the requestor Zip stopped when its mask was reverted
     * (AB#37973) - so read this list as the vocabulary, not as any component's current fields. The
     * entry-form City, State and Zip are now judged by post-hoc patterns and never rewritten;
     * quickEntryFieldConstraints.ts is the single source of truth for what each does.
     */
    readonly type?: QuickEntryFieldKind;
    /**
     * Max input length (AB#35251, AB#35252). The inline row fields - Patient "Last Name/MRN"
     * and the requestor's Fax/Email - had no cap at all, so they accepted unbounded input that
     * the schema then truncates or rejects on save. Callers pass the column-derived caps from
     * quickEntryFieldConstraints.ts (fax = 12, a complete XXX-XXX-XXXX); omitted leaves the
     * field uncapped, as before.
     */
    readonly maxLength?: number;
}

export interface InlineSearchFormProps {
    readonly fields: readonly InlineSearchField[];
    /** Runs the row's search with the (specialist-editable) inline values (AC1/AC2/AC4) - the SAME stateless POST search the automated match uses. */
    readonly onSearch: (values: Readonly<Record<string, string>>) => void;
    /**
     * Optional Cancel/revert (F1c polish): when supplied, a "Cancel" button is
     * rendered next to Search. Clicking it dismisses the inline form WITHOUT
     * running a search, so the row reverts to whatever it was showing before the
     * specialist opened the inline search (the matched-single value, or the
     * Multiple candidate grid). Omitted for the terminal noMatch/serviceUnavailable
     * states, where there is no prior display to revert to.
     */
    readonly onCancel?: () => void;
    /** Row-scoping class (e.g. patient-info-row__inline-search) so each row can target its own inputs in isolation. */
    readonly className?: string;
    /**
     * AB#39478: Classic's Advanced Search fields (Address / Zip / Requestor Name / Contact),
     * hidden behind a toggle. Same field shape as `fields`; omitted leaves the form exactly as
     * it was, so the Patient row is unaffected. Their values ride in the SAME onSearch payload.
     */
    readonly advancedFields?: readonly InlineSearchField[];
}

/**
 * Inline search-entry form rendered ON the Quick Entry row (Spec #34101 (F1c),
 * Lyn O'Connor 2026-07-17: "the search fields should be inline; the results
 * should be a modal"). Pre-filled with the AI-extracted values; the inline
 * Search button re-runs the same stateless POST search the automated match
 * uses. Consumers should pass a `key` derived from the extracted values so the
 * pre-fill resets when a document REPLACE re-extracts new keys.
 */
const InlineSearchForm: React.FC<InlineSearchFormProps> = (props) => {
    const [values, setValues] = useState<Record<string, string>>(() =>
        Object.fromEntries(
            [...props.fields, ...(props.advancedFields ?? [])].map(field => [field.name, field.value ?? ""]),
        ),
    );
    // AB#39478 review: always starts collapsed, matching Classic exactly - a pre-filled advanced
    // value does NOT auto-open it. Whether the advanced values are used at all is now purely
    // "is the section open" (see the Search handler below): collapsed means they are dropped from
    // the search entirely, not just hidden. Typing survives a collapse/re-expand either way -
    // values live in `values` above, not here.
    const [advancedOpen, setAdvancedOpen] = useState(false);
    // AB#37980: a date field's onBlur maps anything it can't parse (including an
    // incomplete masked entry, e.g. "10/22/19__") to INVALID_DATE, which
    // dateFieldValueToString collapses to "" - previously with NO visible
    // feedback, so the specialist believed a DOB was entered while the search
    // ran without one. Tracked per field name so an inline error can be shown
    // instead of the field just going blank.
    const [dateErrors, setDateErrors] = useState<Record<string, boolean>>({});

    const onFieldChange = (name: string, value: string) => {
        setValues(prev => ({ ...prev, [name]: value }));
    };

    const onDateFieldChange = (name: string, value?: Date | string) => {
        setDateErrors(prev => ({ ...prev, [name]: value === INVALID_DATE }));
        onFieldChange(name, dateFieldValueToString(value));
    };

    /**
     * AB#37980 (round 2): onDateFieldChange above is not sufficient on its own. A
     * complete-looking but NON-EXISTENT calendar date - "02/30/1996", "02/29/1997" -
     * makes BOTH of DateField's channels stay silent (see isCompleteCalendarDate),
     * so no value and no INVALID_DATE ever arrive and the box sits there looking
     * correctly filled. Judging the box's own text on blur is the only signal that
     * covers that class.
     *
     * Clearing the value matters as much as showing the error: because the picker
     * never fired, `values[name]` still holds whatever was there before - which for
     * this row is the AI-extracted DOB pre-fill. Without the clear, Search would run
     * on the pre-fill the specialist had just overtyped, while the box displays their
     * (rejected) date.
     *
     * DateField only forwards onBlur when the text is non-empty AND `new Date(text)`
     * parsed - an empty box and a truly unparseable one are already handled by
     * onChange(undefined)/onChange(INVALID_DATE) - and it passes no `name`, so the
     * mirrorData branch this displaces was already unreachable here.
     */
    const onDateFieldBlur = (name: string, text: string) => {
        const valid = isCompleteCalendarDate(text);
        setDateErrors(prev => ({ ...prev, [name]: !valid }));
        if (!valid) {
            onFieldChange(name, "");
        }
    };

    // Extracted so the primary and advanced groups render identically - the advanced
    // fields are the same InlineSearchField shape, just behind the toggle.
    const renderField = (field: InlineSearchField) => {
        const isDate = field.type === "date";
        // DateField renders its own <Form>; a <form> inside a <label> is invalid HTML, so date fields use a <div> wrapper.
        const Wrapper = isDate ? "div" : "label";
        return (
            <Wrapper key={field.name} className="qe-inline-search__field">
                <span className="qe-inline-search__label">{field.label}</span>
                {field.tag ? (
                    <span className={`qe-inline-search__tag qe-inline-search__tag--${field.tag.toLowerCase()}`}>
                        {field.tag}
                    </span>
                ) : null}
                {isDate ? (
                    <DateField
                        placeholder="mm/dd/yyyy"
                        errMsg={dateErrors[field.name] ? INVALID_DATE_MESSAGE : ""}
                        className="qe-inline-search__input"
                        value={values[field.name] || undefined}
                        onChange={(value) => onDateFieldChange(field.name, value)}
                        onBlur={(event) => onDateFieldBlur(field.name, event.target.value)}
                    />
                ) : (
                    <input
                        type="text"
                        className="form-control qe-inline-search__input"
                        data-field={field.name}
                        // Cleared box reads as empty-with-placeholder, not as a blank
                        // gap - and it stays cleared: the value is local state, never
                        // re-derived from the AI-extracted field.value.
                        placeholder={field.label}
                        maxLength={field.maxLength}
                        // AB#35252: numeric keypad on touch devices for phone/fax.
                        inputMode={field.type === "phone" ? "tel" : undefined}
                        value={values[field.name] ?? ""}
                        // AB#35252 / AB#35253: sanitize on the way IN, so letters and
                        // specials never land in state (and so the value handed to the
                        // search is the cleaned one, not the raw keystrokes). One shared
                        // pathway with the create/edit forms - see sanitizeFieldInput.
                        // The pre-filled extracted value is seeded verbatim and only
                        // sanitized once the specialist actually edits it - the box
                        // shows what the document said until then.
                        onChange={(event) => onFieldChange(
                            field.name,
                            sanitizeFieldInput(field.type, event.target.value),
                        )}
                    />
                )}
            </Wrapper>
        );
    };

    return (
        <div className={`qe-inline-search${props.className ? ` ${props.className}` : ""}`}>
            {props.fields.map(renderField)}
            <div className="qe-inline-search__actions">
                <button
                    type="button"
                    className="btn qe-inline-search__btn"
                    // AB#39478 review: matches Classic - the advanced values are only part of the
                    // search when the section is open. Collapsed means they're dropped from the
                    // payload entirely (not merely hidden), so a hidden value can never narrow a
                    // query the specialist can't see, and can never trigger a "N values required"
                    // message with nothing on screen to explain it.
                    onClick={() => {
                        const payload = advancedOpen
                            ? values
                            : Object.fromEntries(
                                  props.fields.map(field => [field.name, values[field.name] ?? ""]),
                              );
                        props.onSearch(payload);
                    }}
                >
                    <img src={magnifyingGlass} alt="" style={{ marginRight: "5px" }} />
                    Search
                </button>
                {props.onCancel ? (
                    <button type="button" className="btn qe-inline-search__cancel-btn" onClick={props.onCancel}>
                        Cancel
                    </button>
                ) : null}
            </div>
            {props.advancedFields?.length ? (
                <>
                    {advancedOpen ? (
                        <div className="qe-inline-search__advanced">
                            {props.advancedFields.map(renderField)}
                        </div>
                    ) : null}
                    {/* Below the fields and full-width, matching Classic's own placement. */}
                    <button
                        type="button"
                        className="qe-inline-search__advanced-toggle"
                        aria-expanded={advancedOpen}
                        onClick={() => setAdvancedOpen(open => !open)}
                    >
                        <span className={`qe-inline-search__advanced-chevron${advancedOpen ? " is-open" : ""}`} aria-hidden="true" />
                        Advanced Search
                    </button>
                </>
            ) : null}
        </div>
    );
};

export default InlineSearchForm;
